import S from "sanctuary";
import { v4 as uuidv4 } from "uuid";
import { createSelector } from "reselect";
import {
  getSortedServiceTypes,
  getSortedPaymentSources,
  getSortedAgencies,
} from "@/components/currentUser/Agencies.state";
import {
  FILTERS,
  DEFAULT_FILTER,
  AGENCIES_FILTER,
  SERVICE_TYPE_FILTER,
  PAYMENT_STATUS_FILTER,
  PAYMENT_CHANNEL_FILTER,
  PAYMENT_METHOD_FILTER,
  PAYMENT_SOURCE_FILTER,
  PAYMENT_MODE_FILTER,
} from "./FiltersForm";
import {
  changeCalendarDates,
  openDatesModal,
} from "@/components/datePicker/DatePicker.state";
import {
  getDefaultSearchDates,
  SEARCH_DATES,
} from "@/components/datePicker/Search.dates.util";
import {
  getTransactionSearchStartDate as getDatePickerStartDate,
  getTransactionSearchEndDate as getDatePickerEndDate,
} from "@/components/datePicker/DatePicker.selectors";
import { getUserTimezone } from "@/components/currentUser/CurrentUser.state";
import { FILTERS_VIEWS, FILTERS_VIEW_VALUES } from "./filterPresentation";

export const CLEAR_FILTERS = "transactionSearch/CLEAR_FILTERS";
export const clearFilters = () => ({
  type: CLEAR_FILTERS,
});

export const TRANSACTION_SEARCH_PENDING =
  "transactionSearch/TRANSACTION_SEARCH_PENDING";
export const transactionSearchPending = () => ({
  type: TRANSACTION_SEARCH_PENDING,
});

// Outcome actions name their search in `meta`, so `payload` stays the error
// envelope the alert reads.
export const SEARCH_REQUEST_FAILED = "transactionSearch/SEARCH_REQUEST_FAILED";
export const searchRequestFailed = ({ error, searchId = null }) => ({
  type: SEARCH_REQUEST_FAILED,
  error: true,
  payload: error,
  meta: { searchId },
});

export const SEARCH_REQUEST_SUCCEEDED = "transactionSearch/SEARCH_REQUEST_SUCCEEDED";
export const searchRequestSucceeded = ({ searchId = null } = {}) => ({
  type: SEARCH_REQUEST_SUCCEEDED,
  meta: { searchId },
});

export const PERFORM_SEARCH = "transactionSearch/PERFORM_SEARCH";
export const performSearch = () => ({
  type: PERFORM_SEARCH,
});

// A caller that has to recognize its own response passes a `searchId`; the epic
// stamps one otherwise. Read through the payload, since some callers are wired
// straight to a DOM handler and arrive with an event.
export const REQUEST_SEARCH = "transactionSearch/REQUEST_SEARCH";
export const requestSearch = (payload) => ({
  type: REQUEST_SEARCH,
  payload: { searchId: payload?.searchId ?? null },
});

export const REQUEST_PAGINATED_SEARCH = "transactionSearch/REQUEST_PAGINATED_SEARCH";
export const requestPaginatedSearch = ({ page }) => ({
  type: REQUEST_PAGINATED_SEARCH,
  payload: { page },
});

// Natural-language → structured-parameter translation
// (POST /transaction-search/translate, backend branch PLFM-4680).
export const TRANSLATE_REQUEST = "transactionSearch/TRANSLATE_REQUEST";
export const translateRequest = ({ query }) => ({
  type: TRANSLATE_REQUEST,
  payload: { query },
});

export const TRANSLATE_SUCCEEDED = "transactionSearch/TRANSLATE_SUCCEEDED";
export const translateSucceeded = ({ result }) => ({
  type: TRANSLATE_SUCCEEDED,
  payload: { result },
});

export const TRANSLATE_FAILED = "transactionSearch/TRANSLATE_FAILED";
export const translateFailed = ({ error }) => ({
  type: TRANSLATE_FAILED,
  error: true,
  payload: error,
});

export const CLEAR_TRANSLATE = "transactionSearch/CLEAR_TRANSLATE";
export const clearTranslate = () => ({
  type: CLEAR_TRANSLATE,
});

// Which filters the container shows (WCHK-2829, WCHK-3037). It lives in the
// reducer so it survives tab navigation, which unmounts the page.
export const SET_FILTERS_VIEW = "transactionSearch/SET_FILTERS_VIEW";
export const setFiltersView = (view) => ({
  type: SET_FILTERS_VIEW,
  payload: { view },
});

export const TRANSACTION_DATE_FILTER = "TRANSACTION_DATE_FILTER";
export const FUNDED_TIMESTAMP_FILTER = "FUNDED_TIMESTAMP_FILTER";

export const OPEN_MODAL = "transactionSearchUi/OPEN_MODAL";
export const openModal = ({ modalKey }) => ({
  type: OPEN_MODAL,
  payload: { modalKey },
});

export const CLOSE_MODAL = "transactionSearchUi/CLOSE_MODAL";
export const closeModal = () => ({
  type: CLOSE_MODAL,
});

export const MODAL_KEYS = {
  DATES_MODAL: "dates_modal",
  SEARCH_MODAL: "search_modal",
};

export const NEXT_PENDING_TEXT_FRAME = "transactionSearchUi/NEXT_PENDING_TEXT_FRAME";
export const nextPendingTextFrame = () => ({
  type: NEXT_PENDING_TEXT_FRAME,
});

export const nextPendingTextFrameMap = {
  Pending: "Pending.",
  "Pending.": "Pending..",
  "Pending..": "Pending...",
  "Pending...": "Pending",
};

const INITIAL_STATE = {
  dropDownCurrentlyOpen: null,
  modalCurrentlyOpen: null,
  searchPendingText: "Pending",
  filtersView: FILTERS_VIEWS.ALL,
  searchRequestPending: false,
  searchRequestError: S.Nothing,
  // The last search to come back: `{ searchId, failed }`, or null before any
  // has. The only signal for "did *my* search settle" (WCHK-3037).
  lastSettledSearch: null,
  translateRequestPending: false,
  translateRequestError: S.Nothing,
  translateResult: S.Nothing,
  searchCriteria: {
    [uuidv4()]: "",
  },
  dateFilter: TRANSACTION_DATE_FILTER,
  searchFilters: {
    [AGENCIES_FILTER]: DEFAULT_FILTER,
    [SERVICE_TYPE_FILTER]: DEFAULT_FILTER,
    [PAYMENT_STATUS_FILTER]: DEFAULT_FILTER,
    [PAYMENT_CHANNEL_FILTER]: DEFAULT_FILTER,
    [PAYMENT_METHOD_FILTER]: DEFAULT_FILTER,
    [PAYMENT_SOURCE_FILTER]: DEFAULT_FILTER,
    [PAYMENT_MODE_FILTER]: DEFAULT_FILTER,
  },
};

const SELECT_DATE_FILTER = "transactionSearch/SELECT_DATE_FILTER";
const selectDateFilter = (filter) => ({
  type: SELECT_DATE_FILTER,
  payload: { filter },
});

const reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload, meta } = action;
  switch (type) {
    case CLEAR_FILTERS:
      return {
        ...state,
        searchFilters: INITIAL_STATE.searchFilters,
      };
    case TRANSACTION_SEARCH_PENDING:
      // A starting search retires the error alerts from the previous one —
      // whatever failed before is no longer what the page is reporting on.
      return {
        ...state,
        searchPendingText: "Pending",
        searchRequestPending: true,
        searchRequestError: S.Nothing,
        translateRequestError: S.Nothing,
      };
    case SEARCH_REQUEST_FAILED:
      return {
        ...state,
        searchRequestPending: false,
        searchRequestError: S.Just(payload),
        lastSettledSearch: { searchId: meta?.searchId ?? null, failed: true },
      };
    case SEARCH_REQUEST_SUCCEEDED:
      return {
        ...state,
        searchRequestPending: false,
        searchRequestError: S.Nothing,
        lastSettledSearch: { searchId: meta?.searchId ?? null, failed: false },
      };
    case TRANSLATE_REQUEST:
      // A fresh submit also retires a lingering /payments failure alert; the
      // page reports on the search now in flight, not the one before it.
      return {
        ...state,
        translateRequestPending: true,
        translateRequestError: S.Nothing,
        searchRequestError: S.Nothing,
      };
    case TRANSLATE_SUCCEEDED:
      return {
        ...state,
        translateRequestPending: false,
        translateRequestError: S.Nothing,
        translateResult: S.Just(payload.result),
      };
    case TRANSLATE_FAILED:
      return {
        ...state,
        translateRequestPending: false,
        translateRequestError: S.Just(payload),
        translateResult: S.Nothing,
      };
    case CLEAR_TRANSLATE:
      return {
        ...state,
        translateRequestPending: false,
        translateRequestError: S.Nothing,
        translateResult: S.Nothing,
      };
    case SET_FILTERS_VIEW: {
      // Read through the payload so a malformed dispatch cannot take the store
      // down: the reducer has to stay total over every action (WCHK-3048
      // review). An unknown view collapses the container.
      const view = FILTERS_VIEW_VALUES.includes(payload?.view)
        ? payload.view
        : FILTERS_VIEWS.COLLAPSED;
      // Bail out when nothing changes so a repeated set cannot churn renders.
      return state.filtersView === view
        ? state
        : {
            ...state,
            filtersView: view,
          };
    }
    case SELECT_DATE_FILTER:
      return {
        ...state,
        dateFilter: payload.filter,
      };
    case OPEN_MODAL:
      return {
        ...state,
        modalCurrentlyOpen: payload.modalKey,
      };
    case CLOSE_MODAL:
      return {
        ...state,
        modalCurrentlyOpen: null,
      };
    case NEXT_PENDING_TEXT_FRAME:
      return {
        ...state,
        searchPendingText: nextPendingTextFrameMap[state.searchPendingText],
      };
    default:
      return state;
  }
};

const getState = (state) => state.transactionSearch;
const getSelectedAgency = (state) =>
  getState(state).forms.filtersForm.agencies.rawValue;
export const getSearchPendingText = (state) => getState(state).searchPendingText;
export const getTransactionSearchStartDate = (state) => getState(state).startDate;
export const getTransactionSearchEndDate = (state) => getState(state).endDate;
export const getTransactionSearchCriteria = (state) =>
  state.transactionSearch?.forms?.criteriaForm;
export const getTransactionSearchRequestPending = (state) =>
  getState(state).searchRequestPending;
export const getTransactionsSearchRequestError = (state) =>
  getState(state).searchRequestError;
export const getTranslateRequestPending = (state) =>
  getState(state).translateRequestPending;
export const getTranslateRequestError = (state) =>
  getState(state).translateRequestError;
export const getTranslateResult = (state) => getState(state).translateResult;
export const getFiltersView = (state) =>
  getState(state).filtersView ?? FILTERS_VIEWS.ALL;
export const getLastSettledSearch = (state) =>
  getState(state).lastSettledSearch ?? null;
// Read straight from the results slice instead of importing its selector:
// that module already imports from this one, and the reverse import would
// close the cycle.
export const getSearchResultsCount = (state) =>
  state.transactionSearchResults?.totalSearchResultsCount ?? 0;
// `transactions` is Nothing until a search lands rows, and the search epic
// resets it when the next search starts, so it marks "the count is real".
export const getSearchResultsLoaded = (state) => {
  const transactions = state.transactionSearchResults?.transactions;
  return !!transactions && S.isJust(transactions);
};

const isCompleteFilter = (filter) =>
  S.and(S.isJust(filter.value))(S.isJust(filter.text));

const toFilters = (fromRecord) =>
  S.map(
    S.pipe([
      S.map(fromRecord),
      S.filter(isCompleteFilter),
      S.map((filter) => ({
        value: filter.value.value,
        text: filter.text.value,
      })),
    ])
  );

const fromAgencyRecord = (agencyRec) => ({
  value: S.prop("name")(agencyRec),
  text: S.prop("name")(agencyRec),
});

const fromServiceTypeRecord = (serviceTypeRec) => ({
  value: S.prop("name")(serviceTypeRec),
  text: S.prop("description")(serviceTypeRec),
});

const fromPaymentSourceRecord = (paymentSourceRec) => ({
  value: S.prop("name")(paymentSourceRec),
  text: S.prop("name")(paymentSourceRec),
});

export const getFilterChoices = createSelector(
  getSortedServiceTypes,
  getSortedPaymentSources,
  getSortedAgencies,
  getSelectedAgency,
  (serviceTypes, paymentSources, agencies, selectedAgencyName) => {
    const agenciesRaw = S.fromMaybe([])(agencies);
    const selectedAgency = agenciesRaw.find(
      (agency) => S.fromMaybe("")(agency.name) === selectedAgencyName
    );
    const selectedAgencyPaymentSources = selectedAgency
      ? S.Just(selectedAgency.paymentSources)
      : paymentSources;
    const selectedAgencyServiceTypes = selectedAgency
      ? S.Just(selectedAgency.serviceTypes)
      : serviceTypes;

    return {
      ...FILTERS,
      [AGENCIES_FILTER]: [
        ...FILTERS[AGENCIES_FILTER],
        ...S.fromMaybe([])(toFilters(fromAgencyRecord)(agencies)),
      ],
      [SERVICE_TYPE_FILTER]: [
        ...FILTERS[SERVICE_TYPE_FILTER],
        ...S.fromMaybe([])(
          toFilters(fromServiceTypeRecord)(selectedAgencyServiceTypes)
        ),
      ],
      [PAYMENT_SOURCE_FILTER]: [
        ...FILTERS[PAYMENT_SOURCE_FILTER],
        ...S.fromMaybe([])(
          toFilters(fromPaymentSourceRecord)(selectedAgencyPaymentSources)
        ),
      ],
    };
  }
);

export const getTransactionDateFilter = (state) =>
  state.transactionSearch.dateFilter;

export const mapStateToProps = (state) => ({
  ...getState(state),
  filterChoices: getFilterChoices(state),
  criteriaValues: getTransactionSearchCriteria(state),
  selectedDateFilter: getTransactionDateFilter(state),
  searchRequestPending: getTransactionSearchRequestPending(state),
  searchStartDate: getDatePickerStartDate(state),
  searchEndDate: getDatePickerEndDate(state),
  translateRequestPending: getTranslateRequestPending(state),
  translateRequestError: getTranslateRequestError(state),
  translateResult: getTranslateResult(state),
  filtersView: getFiltersView(state),
  lastSettledSearch: getLastSettledSearch(state),
  searchResultsLoaded: getSearchResultsLoaded(state),
  // `S.fromMaybe` requires the default to share a type with the Just value,
  // so `null` against a String tz throws sanctuary's type check. Read the
  // Maybe's `.value` directly instead.
  userTimezone: (() => {
    const tz = getUserTimezone(state);
    return S.isJust(tz) ? tz.value : null;
  })(),
});

/**
 * writeCalendarDates
 *
 * One home for a programmatic write to the date picker. `process: false` skips
 * the picker's interactive `processCalendarDates` step (anchor-aware range
 * extension), which can drop or rewrite the dates handed to it — for example
 * when a new translate result lands on top of a prior one. `preset: true`
 * marks the write as programmatic, the same way the picker's own quick ranges
 * are written. Every caller goes through here so a change to that contract has
 * one place to land (WCHK-3083 review).
 *
 * @param {object} dates - `{ startDate, endDate }` as moments or null.
 * @returns {object} - The date picker action.
 */
const writeCalendarDates = (dates) => changeCalendarDates(dates, false, true);

/**
 * resetSearchDates
 *
 * "Clear" means the default range — a year ago through today — everywhere in
 * the natural-language experience, not an empty range (WCHK-3083).
 *
 * @returns {object} - The date picker action.
 */
const resetSearchDates = () => writeCalendarDates(getDefaultSearchDates());

export const mapDispatchToProps = (dispatch) => ({
  requestSearch: (payload) => dispatch(requestSearch(payload)),
  transactionSearchPending: () => dispatch(transactionSearchPending()),
  searchRequestFailed: (payload) => dispatch(searchRequestFailed(payload)),
  searchRequestSucceeded: (payload) => dispatch(searchRequestSucceeded(payload)),
  translateRequest: (payload) => dispatch(translateRequest(payload)),
  clearTranslate: () => dispatch(clearTranslate()),
  setFiltersView: (view) => dispatch(setFiltersView(view)),
  openModal: (payload) => dispatch(openModal(payload)),
  closeModal: () => dispatch(closeModal()),
  clearFilters: () => {
    dispatch(clearFilters());
    // Returns the date range to the default rather than emptying it. The
    // page runs the search afterwards.
    dispatch(resetSearchDates());
  },
  selectDateFilter: (payload) => dispatch(selectDateFilter(payload)),
  // The date filter pill opens the calendar modal directly — it has no panel
  // of its own. The start token mirrors what clicking the start date field
  // does on the legacy page (WCHK-3083 review).
  openDatesCalendar: () => dispatch(openDatesModal(SEARCH_DATES.START)),
  changeCalendarDates: (dates) => dispatch(changeCalendarDates(dates, true)),
  // Programmatic setter for the natural-language flow.
  setNlSearchDates: (dates) => dispatch(writeCalendarDates(dates)),
  // The one call that means "put the date range back to the default".
  resetSearchDatesToDefault: () => dispatch(resetSearchDates()),
});
export default reducer;
